import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ImageDown } from "lucide-react";
import type { HeadshotProposal } from "@event-planner/shared";
import {
  useHeadshotProposals,
  useHeadshotImportApply,
} from "../../hooks/useHeadshotImport";
import { useSnackbar } from "../../context/SnackbarContext";

/**
 * Review-first pull from the Headshot Library (ADR-178, production
 * retarget ADR-181).
 *
 * The library is an external standalone system: this dialog only proposes
 * and imports — it never writes to the library. Matched files render as
 * checkable rows (rows whose person already has a photo default UNCHECKED;
 * checking one is the explicit overwrite consent). Unmatched files arrive
 * as a COUNT, not rows: in production most of the library matches nobody
 * here, because it covers Flat Rock Global's full contact sync.
 */

interface HeadshotPullDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful import so the parent can refetch persons. */
  onImported: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  imported: "Imported",
  "skipped-mismatch": "Skipped — no longer matches",
  "skipped-too-large": "Skipped — too large",
  "skipped-unsupported-type": "Skipped — unsupported type",
  failed: "Failed",
};

const MATCHED_BY_LABELS: Record<string, string> = {
  hubspotid: "HubSpot",
  email: "Email",
  repcrd: "CRD",
};

/**
 * Default checks: no-photo matches only, and when several files match the
 * SAME person, only the first — a person has one photo slot, so
 * default-checking every duplicate would silently make the last import win.
 */
function initialSelection(proposals: HeadshotProposal[]): Set<string> {
  const selected = new Set<string>();
  const personsTaken = new Set<string>();
  for (const p of proposals) {
    if (!p.match || p.conflict || p.match.hasExistingPhoto) continue;
    if (personsTaken.has(p.match.personId)) continue;
    personsTaken.add(p.match.personId);
    selected.add(p.driveItemId);
  }
  return selected;
}

export function HeadshotPullDialog({
  open,
  onClose,
  onImported,
}: HeadshotPullDialogProps) {
  const { showSuccess, showError } = useSnackbar();
  const {
    proposals,
    totalFiles,
    unmatchedCount,
    libraryUrl,
    loading,
    unconfigured,
    error: loadError,
    load,
  } = useHeadshotProposals();
  const { apply, applying, progress, result, error: applyError, reset } =
    useHeadshotImportApply();

  const [checked, setChecked] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      reset();
      load();
    }
  }, [open, load, reset]);

  const matched = useMemo(
    () => proposals.filter((p) => p.match !== null),
    [proposals],
  );
  const conflicts = useMemo(
    () => proposals.filter((p) => p.conflict !== null),
    [proposals],
  );
  // Later files matching a person someone above already matched — flagged so
  // the organizer sees why they weren't default-checked.
  const duplicateIds = useMemo(() => {
    const seen = new Set<string>();
    const dups = new Set<string>();
    for (const p of matched) {
      const personId = p.match!.personId;
      if (seen.has(personId)) dups.add(p.driveItemId);
      else seen.add(personId);
    }
    return dups;
  }, [matched]);

  useEffect(() => {
    setChecked(initialSelection(proposals));
  }, [proposals]);

  const allChecked = matched.length > 0 && matched.every((p) => checked.has(p.driveItemId));

  const toggle = (driveItemId: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(driveItemId)) next.delete(driveItemId);
      else next.add(driveItemId);
      return next;
    });
  };

  const handleApply = async () => {
    const items = matched
      .filter((p) => checked.has(p.driveItemId) && p.match)
      .map((p) => ({ driveItemId: p.driveItemId, personId: p.match!.personId }));
    if (items.length === 0) return;
    const response = await apply(items);
    if (response) {
      if (response.imported > 0) {
        showSuccess(
          `Imported ${response.imported} headshot${response.imported === 1 ? "" : "s"}` +
            (response.failed ? ` (${response.failed} skipped/failed)` : ""),
        );
        onImported();
      } else {
        showError("Nothing was imported — see the per-file results.");
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Pull headshots from the library</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Stack alignItems="center" py={5}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" mt={2}>
              Reading the headshot library…
            </Typography>
          </Stack>
        )}

        {!loading && unconfigured && (
          <Alert severity="info" data-testid="headshots-unconfigured">
            {unconfigured}
          </Alert>
        )}

        {!loading && loadError && <Alert severity="error">{loadError}</Alert>}

        {!loading && !unconfigured && !loadError && result && (
          <Stack spacing={1} data-testid="headshots-import-results">
            {/* A chunk failure mid-batch still leaves earlier imports done —
                show the error alongside the partial results. */}
            {applyError && <Alert severity="error">{applyError}</Alert>}
            <Typography variant="subtitle2">
              {result.imported} imported · {result.failed} skipped or failed
            </Typography>
            {result.results.map((r) => (
              <Stack
                key={r.driveItemId}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Chip
                  size="small"
                  label={STATUS_LABELS[r.status] ?? r.status}
                  color={r.status === "imported" ? "success" : "warning"}
                  variant="outlined"
                />
                {r.linked && (
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label="Linked to HubSpot"
                  />
                )}
                <Typography variant="body2">
                  {matched.find((p) => p.driveItemId === r.driveItemId)?.fileName ??
                    r.driveItemId}
                </Typography>
                {r.message && (
                  <Typography variant="caption" color="text.secondary">
                    {r.message}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        )}

        {!loading && !unconfigured && !loadError && !result && (
          <Stack spacing={2}>
            {totalFiles === 0 && (
              <Alert severity="info">The library has no image files yet.</Alert>
            )}

            {matched.length > 0 && (
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <Checkbox
                    size="small"
                    checked={allChecked}
                    indeterminate={checked.size > 0 && !allChecked}
                    onChange={() =>
                      setChecked(
                        allChecked
                          ? new Set()
                          : new Set(matched.map((p) => p.driveItemId)),
                      )
                    }
                    inputProps={{ "aria-label": "Select all matched headshots" }}
                  />
                  <Typography variant="subtitle2">
                    Matched ({matched.length})
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Importing a photo also links the person to the file&apos;s
                  HubSpot contact if they aren&apos;t linked yet.
                </Typography>
                <Stack spacing={0.5}>
                  {matched.map((p) => (
                    <Stack
                      key={p.driveItemId}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      data-testid={`headshot-row-${p.driveItemId}`}
                    >
                      <Checkbox
                        size="small"
                        checked={checked.has(p.driveItemId)}
                        onChange={() => toggle(p.driveItemId)}
                        inputProps={{
                          "aria-label": `Import headshot for ${p.match!.personDisplayName}`,
                        }}
                      />
                      <Avatar
                        src={p.thumbnailUrl ?? undefined}
                        sx={{ width: 40, height: 40 }}
                      >
                        {p.match!.personDisplayName[0] ?? "?"}
                      </Avatar>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2" noWrap>
                          {p.match!.personDisplayName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          display="block"
                        >
                          {p.fileName}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          MATCHED_BY_LABELS[p.match!.matchedBy] ??
                          p.match!.matchedBy
                        }
                      />
                      {p.hubspotId && p.match!.matchedBy !== "hubspotid" && (
                        <Tooltip title="This person isn't linked to HubSpot yet — importing links them to this file's contact">
                          <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            label="Will link"
                          />
                        </Tooltip>
                      )}
                      {duplicateIds.has(p.driveItemId) && (
                        <Tooltip title="Another file above also matches this person — only one photo can be kept">
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label="Duplicate"
                          />
                        </Tooltip>
                      )}
                      {p.match!.hasExistingPhoto ? (
                        <Tooltip title="This person already has a photo — checking the box replaces it">
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label="Has photo"
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label="No photo"
                        />
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            {conflicts.length > 0 && (
              <Alert severity="warning">
                {conflicts.length} file{conflicts.length === 1 ? "" : "s"} with
                conflicting matches (identity keys — HubSpot ID, email, CRD —
                point at different people) — resolve in the library:
                {conflicts.map((p) => (
                  <Typography key={p.driveItemId} variant="caption" display="block">
                    {p.fileName}: {p.conflict}
                  </Typography>
                ))}
              </Alert>
            )}

            {unmatchedCount > 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid="headshots-unmatched-count"
              >
                {unmatchedCount} of {totalFiles} library files don&apos;t match
                anyone in this app — expected: the library covers Flat Rock
                Global&apos;s full contact sync.
                {libraryUrl && (
                  <>
                    {" "}
                    <Link href={libraryUrl} target="_blank" rel="noopener">
                      Open the library
                    </Link>
                  </>
                )}
              </Typography>
            )}

            {applying && progress && (
              <Alert severity="info" data-testid="headshots-import-progress">
                Importing batch{" "}
                {Math.min(progress.done + 1, progress.total)} of{" "}
                {progress.total}…
              </Alert>
            )}

            {applyError && <Alert severity="error">{applyError}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="headshot-pull-close">
          {result ? "Done" : "Cancel"}
        </Button>
        {!result && !unconfigured && !loadError && (
          <Button
            variant="contained"
            startIcon={
              applying ? <CircularProgress size={16} /> : <ImageDown size={18} />
            }
            disabled={applying || checked.size === 0}
            onClick={handleApply}
            data-testid="headshot-pull-apply"
          >
            Import {checked.size || ""} photo{checked.size === 1 ? "" : "s"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
